/**
 * Copyright (c) 2025 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	robin herrmann - itemis AG
 *
 */

import { dia, mvc, ui } from "@clientio/rappid";
import { PreferenceStore } from "../services/preference-store";

export class NameTextEditor {
  private view: dia.CellView = null;
  private textWrap: string | null = null;
  events: string[] = [];
  eventCodes: string[] = [];

  constructor(private commandManager: dia.CommandManager) {
    ui.TextEditor.on("open", (_, cellView: dia.CellView) => {
      this.open(cellView);
    });

    ui.TextEditor.on("close", (_, cellView: dia.CellView) => {
      this.close(cellView);
    });
  }

  dispose() {
    ui.TextEditor.off("open");
    ui.TextEditor.off("close");
    this.events.forEach((ev) => {
      ui.TextEditor.off(ev);
    });
    this.events = [];

    this.view = null;
  }

  create(view: dia.CellView, selectorName: "name" | "label", eventCodes: string[] = ["Enter", "Escape"]) {
    this.eventCodes = eventCodes;

    const selector = "text[joint-selector='" + selectorName + "']";
    const element = view.vel.findOne(selector);
    if (!element) return;
    const editor = ui.TextEditor.edit(element.node, {
      cellView: view,
      textProperty: ["attrs", selectorName, "text"],
      onKeydown: this.onKeydown.bind(this),
      onOutsidePointerdown: this.onOutsidePointerdown.bind(this),
    });

    editor.select(editor.getSelectionLength(), editor.getSelectionLength());
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(eventName: string, callback: mvc.EventHandler, context?: any) {
    this.events.push(eventName);
    ui.TextEditor.on(eventName, callback, context);
  }

  private onKeydown(evt: KeyboardEvent) {
    this.eventCodes.forEach((ec) => {
      if (evt.code === ec) {
        evt.stopPropagation();
        ui.TextEditor.close();
        return true;
      }
    });
  }

  private onOutsidePointerdown(evt: PointerEvent) {
    const target = evt.target;
    if (target instanceof Element) {
      const selector = target.getAttribute("joint-selector");
      const contains = this.view.el.contains(target);
      if (contains && selector === "namebg") {
        return;
      }
    }

    evt.stopPropagation();
    ui.TextEditor.close();
  }

  private open(cellView: dia.CellView) {
    this.commandManager.initBatchCommand();

    this.view = cellView;

    this.textWrap = cellView.model.attr("name/textWrap");
    if (this.textWrap) {
      cellView.model.attr("name/textWrap", null, { async: false });
    }

    const model = cellView.model;
    if (model.attr(["namebg", "stroke"])) {
      if (PreferenceStore.INSTANCE.isDark) {
        model.attr(["namebg", "stroke"], "var(--state-dark-stroke-color)");
      } else {
        model.attr(["namebg", "stroke"], "var(--state-light-stroke-color)");
      }
    }

    const nameBg = cellView.vel.findOne("rect[joint-selector='namebg']");
    if (nameBg) {
      nameBg.node.style.cursor = "text";
    }
  }

  private close(cellView: dia.CellView) {
    if (cellView.model.attr(["namebg", "stroke"])) {
      cellView.model.attr(["namebg", "stroke"], "transparent");
    }

    const nameBg = cellView.vel.findOne("rect[joint-selector='namebg']");
    if (nameBg) {
      nameBg.node.style.cursor = "grab";
    }

    if (this.textWrap) {
      cellView.model.attr("name/textWrap", this.textWrap);
    }

    this.events.forEach((ev) => {
      ui.TextEditor.off(ev);
    });

    this.commandManager.storeBatchCommand();

    this.events = [];
    this.view = null;
    this.textWrap = null;
  }
}
